import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useMedia, { MOBILE_SIZE } from 'hooks/useMediaQuery';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectLEMPGAuth, selectUserAccount } from 'store/slices/currentUser/currentUser.selector';
import { selectAccessToken, selectJWTToken } from 'store/slices/auth/auth.selector';
import { changeMenu } from 'store/slices/menu/menuSlice';
import { updateUserAttribute } from 'store/slices/userManage/userManageThunk';
import { fetchCurrentUserInfo } from 'store/slices/currentUser/currentUserThunk';
import { updateCurrentUser, updateLEMPGAuth } from 'store/slices/currentUser/currentUserSlice';
import { updateActiveGroupId } from 'store/slices/cognito/cognitoSlice';
import { useLazyGetSettingsQuery } from 'services/settingsApi';
import DashboardView from './Dashboard.view';
import { DashboardProps } from './Dashboard.props';
import {
  ProgramCodes,
  ProgramSites,
  ProgramTitles,
  SiteStatusCodes,
  ToastStatus,
} from 'common/enum';
import { PayloadAttribute } from 'interfaces/attribute.interface';
import { ToastMessage } from 'components/primitives';
import {
  AppCodeIdToken,
  DefineUserAttributesData,
  findKeyIndAttribute,
  SetNewAttributesData,
} from 'common/utils';

interface GroupAttributeItem {
  S?: SiteStatusCodes;
  P?: string[];
}

const Dashboard = () => {
  const { isMobileDevice, windowDimensions } = useMedia();
  const isMobile = isMobileDevice || (windowDimensions?.width ?? 1000) < MOBILE_SIZE;
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUserAccount);
  const activeGroupId = (currentUser && currentUser?.pvActiveGroupID) || 0; // useAppSelector(selectActiveGroupId);
  const accessToken = useAppSelector(selectAccessToken);
  const jwtToken = useAppSelector(selectJWTToken);
  const isLEMPGAuth = useAppSelector(selectLEMPGAuth);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [detailsValue, setDetailsValue] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [toggleAllSites, setToggleAllSites] = useState(false);
  const [otpModal, setOtpModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [groupName, setGroupName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [mapIDs, setMapIDs] = useState<SettingsData[]>([]);

  // RTK Hook
  const [getSettings] = useLazyGetSettingsQuery();

  const selectedMapID = useMemo(() => {
    let SCEMDMapID = 'e8336b1697bd42ce840146e0b8930f61';
    if (mapIDs && mapIDs.length > 0) {
      const pvSettingValue = mapIDs.find(
        (map) => map.pvGroupID && parseInt(map.pvGroupID, 10) === 1
      )?.pvSettingValue;
      if (pvSettingValue) SCEMDMapID = pvSettingValue;
    }
    if (mapIDs && mapIDs.length > 0 && activeGroupId > 0) {
      const mapID = mapIDs.find(
        (map) => map.pvGroupID && parseInt(map.pvGroupID, 10) === activeGroupId
      );
      return mapID ? mapID.pvSettingValue : SCEMDMapID;
    }
    return SCEMDMapID;
  }, [mapIDs, activeGroupId]);

  useEffect(() => {
    if (currentUser && currentUser?.currentGroups && currentUser?.currentGroups.length) {
      const filterGroup = currentUser.currentGroups.find((g) => g.i === activeGroupId);
      if (filterGroup && filterGroup.n) setGroupName(filterGroup.n);
    }
  }, [activeGroupId, currentUser]);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      dispatch(fetchCurrentUserInfo(currentUser.id))
        .unwrap()
        .then((userRecord) => {
          const copyUserRecord = { ...userRecord };
          copyUserRecord.attributes = DefineUserAttributesData(userRecord?.attributes || []);
          if (copyUserRecord && copyUserRecord.id) {
            if (
              copyUserRecord &&
              copyUserRecord?.currentGroups &&
              !copyUserRecord.pvActiveGroupID
            ) {
              // *: Check if user has no active group
              const currentGroups = copyUserRecord.currentGroups;
              const activeGroup = currentGroups[0].i;
              dispatch(updateActiveGroupId({ id: activeGroup }));
              const updateUser = { ...copyUserRecord, pvActiveGroupID: activeGroup };
              dispatch(updateCurrentUser(updateUser));
            }
            const userAttributes = copyUserRecord.attributes || [];
            const updateUser = { ...copyUserRecord, attributes: userAttributes };
            dispatch(updateCurrentUser(updateUser));
            const fullName =
              (copyUserRecord &&
                copyUserRecord?.pvPersonGivenName &&
                copyUserRecord?.pvPersonSurName &&
                `${copyUserRecord?.pvPersonGivenName} ${copyUserRecord?.pvPersonSurName}`) ||
              '';
            const userEmail = copyUserRecord.email ?? '';

            // Define base userPayload that can be used in all scenarios
            const baseUserPayload: PayloadAttribute = {
              id: copyUserRecord.id,
              groupId: activeGroupId,
              attributes: { attributes: userAttributes },
              isRequest: false,
              isDeny: false,
              isGrant: false,
              info: {
                email: userEmail,
                name: fullName,
                site: '',
              },
            };

            // Ensure access to default programs (DB30, MP30)
            const defaultPrograms = [ProgramCodes.DB30, ProgramCodes.MP30];
            let updatedAttributes = userAttributes;
            const programsUpdated: string[] = [];

            // Check and grant access to each default program
            defaultPrograms.forEach((program) => {
              const programIndex = findKeyIndAttribute(updatedAttributes, program);

              // Check if program exists in attributes
              if (programIndex === -1) {
                // Program doesn't exist at all, need to add it
                console.log(`Program ${program} not found in attributes, adding it`);
                updatedAttributes = SetNewAttributesData(
                  updatedAttributes,
                  -1, // New program
                  program,
                  SiteStatusCodes.ACTIVE,
                  activeGroupId,
                  false,
                  'G'
                );
                programsUpdated.push(program);
                return; // Continue to next program
              }

              // Program exists, check if it has active access for this group
              const programData = updatedAttributes[programIndex][program];

              // Skip programs with DENY status (access was intentionally revoked)
              const hasDenyStatus = programData.some((item) => item.S === SiteStatusCodes.DENY);
              if (hasDenyStatus) return;

              // Check if any item has ACTIVE status directly
              const hasRootActiveStatus = programData.some(
                (item) => item.S === SiteStatusCodes.ACTIVE
              );

              // Check if any item has this group with ACTIVE status
              const hasGroupActiveStatus = programData.some(
                (item) =>
                  item[activeGroupId] &&
                  Array.isArray(item[activeGroupId]) &&
                  (item[activeGroupId] as GroupAttributeItem[]).some(
                    (g) => g.S === SiteStatusCodes.ACTIVE
                  )
              );

              // If neither condition is true, we need to grant access
              if (!hasRootActiveStatus && !hasGroupActiveStatus) {
                console.log(`Granting access to ${program} for group ${activeGroupId}`);
                updatedAttributes = SetNewAttributesData(
                  updatedAttributes,
                  programIndex,
                  program,
                  SiteStatusCodes.ACTIVE,
                  activeGroupId,
                  false,
                  'G'
                );
                programsUpdated.push(program);
              }
              // else {
              //   console.log(`Program ${program} already has active access`);
              // }
            });
            // Only dispatch if we actually updated any programs
            if (programsUpdated.length > 0) {
              // Get the title of the first updated program (or fallback to a default)
              const primaryProgram = programsUpdated[0];
              const programTitle = ProgramTitles[primaryProgram as keyof typeof ProgramTitles];

              // Always dispatch the update for default programs
              const updatedUserPayload = {
                ...baseUserPayload,
                attributes: { attributes: updatedAttributes },
                info: {
                  ...baseUserPayload.info,
                  site: programTitle,
                },
              };

              dispatch(updateUserAttribute(updatedUserPayload))
                .unwrap()
                .catch((error) => {
                  console.error('Failed to grant default program access', error);
                });
            }
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to load current user info on dashboard', error);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      getSettings({ pvSettingType: 'basemapID' }).then((resp) => {
        if (resp.data && resp.data.length > 0) {
          setMapIDs(resp.data);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenu = () => {
    dispatch(changeMenu({ BtnAction: true }));
  };

  const openPage = async (appCode: string) => {
    setSelectedApp(appCode);
    const indexOf = Object.keys(ProgramSites).findIndex((val) => val === appCode);
    const programValues = Object.values(ProgramSites);
    const siteURL = programValues[indexOf];

    if (appCode === ProgramCodes.LEMPG && !isLEMPGAuth) {
      setOtpModal(true);
    } else {
      if (indexOf > -1 && accessToken && currentUser) {
        let url: string;
        if (AppCodeIdToken(appCode)) {
          url = `${siteURL}?id_token=${jwtToken}`;
          url += appCode === ProgramCodes.MP30 ? `&webmap=${selectedMapID}` : '';
        } else if (appCode === ProgramCodes.HURH) {
          url = siteURL;
        } else {
          url = `${siteURL}?access_token=${accessToken}&user_id=${currentUser.id}`;
        }
        const newWindow = window.open(url);
        if (newWindow) {
          newWindow.focus();
        }
      }
    }
  };

  const onChangeDetails = (value: string) => setDetailsValue(value);

  const onSubmitRequest = async (confirm: boolean, id: number, appCode: ProgramCodes | string) => {
    if (confirm) {
      if (id && appCode && currentUser) {
        const currentAttributes = DefineUserAttributesData(currentUser.attributes || []);
        if (currentAttributes && activeGroupId) {
          const indexOfA = findKeyIndAttribute(currentAttributes, appCode);
          const newAttribute = SetNewAttributesData(
            currentAttributes,
            indexOfA,
            appCode,
            SiteStatusCodes.REQUEST,
            activeGroupId,
            false
          );

          // *: Get App Title
          let appTitle: string;
          if (appCode in ProgramTitles) {
            appTitle = ProgramTitles[appCode as ProgramCodes];
          } else {
            appTitle = Object.values(ProgramTitles)[indexOfA];
          }
          const payload: PayloadAttribute = {
            id,
            groupId: activeGroupId,
            attributes: { attributes: newAttribute },
            isRequest: true,
            isDeny: false,
            isGrant: false,
            info: {
              message: detailsValue.replace(/\r\n|\r|\n/g, '<br />'),
              name: `${currentUser.pvPersonGivenName} ${currentUser.pvPersonSurName}`,
              cellPhone: currentUser.pvMobilePhone || '',
              workPhone: currentUser.pvOfficePhone || '',
              email: currentUser.email || '',
              site: appTitle || '',
            },
          };
          setIsProcessing(true);
          await dispatch(updateUserAttribute(payload))
            .unwrap()
            .then(async () => {
              await dispatch(fetchCurrentUserInfo(payload.id))
                .unwrap()
                .then((userRecord) => {
                  if (userRecord && userRecord.id) {
                    dispatch(updateCurrentUser(userRecord));
                  }
                  toast.info(
                    <ToastMessage
                      status={ToastStatus.SUCCESS}
                      message='Your request access has been sent!'
                    />
                  );
                  setIsProcessing(false);
                })
                .catch(() => {
                  toast.info(
                    <ToastMessage status={ToastStatus.ERROR} message='Request sent failed!' />
                  );
                  setIsProcessing(false);
                });
            })
            .catch(() => {
              toast.info(
                <ToastMessage status={ToastStatus.ERROR} message='Request sent failed!' />
              );
              setIsProcessing(false);
            });
        }
        setOpenModal(false);
        setDetailsValue('');
      }
    } else {
      setOpenModal(false);
      setDetailsValue('');
    }
  };

  const handleToggle = (_event: React.MouseEvent<HTMLElement>, newValue: boolean) => {
    setToggleAllSites(newValue);
  };

  const handleOTPModal = (confirmation: boolean) => {
    if (confirmation && selectedApp) {
      setOtpModal(false);
      if (selectedApp === ProgramCodes.LEMPG) dispatch(updateLEMPGAuth(true));
      const indexOf = Object.keys(ProgramSites).findIndex((val) => val === selectedApp);
      const programValues = Object.values(ProgramSites);
      const siteURL = programValues[indexOf];
      if (indexOf > -1 && accessToken && currentUser) {
        const url = `${siteURL}?id_token=${jwtToken}`;
        const newWindow = window.open(url);
        if (newWindow) newWindow.focus();
      }
    } else {
      setOtpModal(false);
    }
  };

  const combineProps: DashboardProps = {
    isMobile,
    isLoading,
    toggleAllSites,
    openModal,
    detailsValue,
    isProcessing,
    activeGroupId,
    currentUser,
    jwtToken,
    otpModal,
    groupName,
    openPage,
    handleMenu,
    handleOTPModal,
    onChangeDetails,
    onSubmitRequest,
    handleToggle,
    handleOpenModal: () => setOpenModal(!openModal),
  };

  return <DashboardView {...combineProps} />;
};

export default Dashboard;
